import React, { useState, useEffect } from 'react';
import { Calendar } from 'lucide-react';

interface DateInputProps {
    value: string | null;
    onChange: (isoStringOrNull: string | null) => void;
    name?: string;
    label?: string;
    disabled?: boolean;
    required?: boolean;
}

const DateInput: React.FC<DateInputProps> = ({ value, onChange, name, label, disabled, required }) => {
    const toInputValue = (iso: string | null) => {
        if (!iso) return '';
        // Parse UTC date without timezone conversion
        const d = new Date(iso);
        const y = d.getUTCFullYear();
        const m = String(d.getUTCMonth() + 1).padStart(2, '0');
        const day = String(d.getUTCDate()).padStart(2, '0');
        return `${y}-${m}-${day}`;
    };

    // Use local state to track the input value
    const [inputValue, setInputValue] = useState(toInputValue(value));

    // Update local state when prop value changes externally
    useEffect(() => {
        setInputValue(toInputValue(value));
    }, [value]);

    const handleInputChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const newValue = e.target.value;
        setInputValue(newValue);

        // Only propagate to parent if the date is empty or complete and valid
        if (!newValue) {
            onChange(null);
            return;
        }

        // Only process complete dates in YYYY-MM-DD format
        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(newValue)) {
            // If incomplete, don't propagate to parent yet
            return;
        }

        const [y, m, d] = newValue.split('-').map(Number);

        // Validate the date components
        if (isNaN(y) || isNaN(m) || isNaN(d)) {
            return;
        }

        // Validate ranges
        if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2100) {
            return;
        }

        // Create date in UTC to avoid timezone shifts
        const utcDate = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));

        // Validate that the date is valid (e.g., not Feb 31)
        if (utcDate.getUTCDate() !== d || utcDate.getUTCMonth() !== m - 1 || utcDate.getUTCFullYear() !== y) {
            return;
        }

        // Only propagate valid, complete dates
        onChange(utcDate.toISOString());
    };

    const handleBlur = () => {
        // When user leaves the field (TAB, click outside, etc.)
        // Reset to the last valid value from props if current input is incomplete/invalid
        if (!inputValue) {
            // Empty is valid
            return;
        }

        const dateRegex = /^\d{4}-\d{2}-\d{2}$/;
        if (!dateRegex.test(inputValue)) {
            // Incomplete date - reset to last valid value
            setInputValue(toInputValue(value));
            return;
        }

        const [y, m, d] = inputValue.split('-').map(Number);

        // Validate the date components
        if (isNaN(y) || isNaN(m) || isNaN(d)) {
            setInputValue(toInputValue(value));
            return;
        }

        // Validate ranges
        if (m < 1 || m > 12 || d < 1 || d > 31 || y < 1900 || y > 2100) {
            setInputValue(toInputValue(value));
            return;
        }

        // Create date in UTC to avoid timezone shifts
        const utcDate = new Date(Date.UTC(y, m - 1, d, 0, 0, 0, 0));

        // Validate that the date is valid (e.g., not Feb 31)
        if (utcDate.getUTCDate() !== d || utcDate.getUTCMonth() !== m - 1 || utcDate.getUTCFullYear() !== y) {
            setInputValue(toInputValue(value));
            return;
        }
    };

    return (
        <div className="relative">
            {label && <label htmlFor={name} className="block text-sm font-medium text-secondary-700 mb-1">{label}</label>}
            <Calendar className={`absolute right-3 h-4 w-4 text-secondary-400 pointer-events-none ${label ? 'top-8' : 'top-1/2 transform -translate-y-1/2'}`} />
            <input
                type="date"
                name={name}
                value={inputValue}
                onChange={handleInputChange}
                onBlur={handleBlur}
                className="input-field pr-10"
                disabled={disabled}
                required={required}
            />
        </div>
    );
};

export default DateInput;


